import { Component, Input, Output, EventEmitter, forwardRef, ViewChild, ChangeDetectorRef, OnChanges, OnDestroy, AfterViewInit, HostListener, ElementRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AutocompleteComponent } from 'angular-ng-autocomplete';

@Component({
  selector: 'app-custom-autocomplete',
  templateUrl: './custom-autocomplete.component.html',
  styleUrls: ['./custom-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomAutocompleteComponent),
      multi: true
    }
  ]
})
export class CustomAutocompleteComponent implements ControlValueAccessor, OnChanges, OnDestroy, AfterViewInit {

  @ViewChild('autoComplete') autoComplete!: AutocompleteComponent;

  // Configuración del autocomplete
  @Input() data: any[] = [];
  @Input() searchKeyword: string = 'nombre';
  @Input() placeholder: string = 'Buscar...';
  @Input() isLoading: boolean = false;
  @Input() debounceTime: number = 400;
  @Input() minQueryLength: number = 0;
  @Input() disabled: boolean = false;
  @Input() uppercase: boolean = false;
  @Input() isInvalid: boolean = false;
  private _value: string = '';
  private _viewInitialized = false;

  /** Valor de texto a mostrar en el input (para restaurar al recrear el componente) */
  @Input() set value(val: string) {
    const text = (val !== undefined && val !== null) ? String(val).replace(/<[^>]*>/g, '') : '';
    this._value = text;

    // Solo aplicar si hay texto Y el usuario no está escribiendo activamente
    if (text && !this.currentQuery) {
      this.selectedValue = text;
      this.lastSelectedValue = text;
      if (this._viewInitialized && this.autoComplete) {
        this.applyValueToInput(text);
      }
      // Si la vista no está inicializada, selectedValue ya tiene el texto
      // y [initialValue] lo tomará al renderizarse ng-autocomplete
    }
  }

  // Template personalizado (opcional)
  @Input() showSecondaryText: boolean = true;
  @Input() secondaryTextField: string = 'direccion';
  @Input() iconClass: string = 'ri-map-pin-line';
  @Input() allowFreeText: boolean = false;

  // Eventos
  @Output() selected = new EventEmitter<any>();
  @Output() inputChanged = new EventEmitter<string>();
  @Output() inputCleared = new EventEmitter<void>();
  @Output() freeTextEntered = new EventEmitter<string>();

  // Control interno
  selectedValue: string = '';
  internalData: any[] = [];
  private backupData: any[] = [];
  private currentQuery: string = '';
  private lastSelectedValue: string = '';
  private pendingValue: string | null = null; // valor pendiente hasta que ViewChild esté listo

  /**
   * Flag anti-doble-disparo en onSelected.
   * ng-autocomplete.ngOnChanges llama a setInitialValue(initialValue) en CADA cambio de
   * cualquier @Input. Al asignar selectedValue en onSelected, Angular detecta el cambio en
   * [initialValue] → ngOnChanges → setInitialValue → select(string) → selected.emit(string)
   * → segunda llamada a onSelected. El flag evita reprocesar.
   */
  private _isSelecting = false;

  /**
   * Flag para detectar clicks dentro del panel (ítems no focusables → relatedTarget=null).
   * Cuando el usuario hace click en un ítem del dropdown:
   *   blur dispara ANTES que click → relatedTarget=null → isInsideComponent=false
   * Sin este flag, onFocusOut trataría el blur como "salida sin selección" y emitiría
   * freeTextEntered con el texto escrito, sobreescribiendo el valor seleccionado a los 200ms.
   */
  private _isMouseDownInPanel = false;

  /** Timer del applyValueToInput en onFocusOut (allowFreeText). Se cancela en onSelected. */
  private _freeTextTimer: any = null;

  // ControlValueAccessor
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef, private elementRef: ElementRef) {}

  ngAfterViewInit() {
    this._viewInitialized = true;

    // Detectar mousedown dentro del componente para distinguir clicks en ítems del dropdown
    // (que son <div> no focusables) de una salida real del componente.
    const el = this.elementRef.nativeElement;
    el.addEventListener('mousedown', () => { this._isMouseDownInPanel = true; });
    el.addEventListener('mouseup',   () => {
      // Pequeño delay para que onFocusOut pueda leer el flag antes de que se resetee
      setTimeout(() => { this._isMouseDownInPanel = false; }, 300);
    });

    if (this.pendingValue !== null && this.autoComplete) {
      this.applyValueToInput(this.pendingValue);
      this.pendingValue = null;
    } else if (this.selectedValue && this.autoComplete) {
      this.applyValueToInput(this.selectedValue);
    }
  }

  /**
   * Cuando el foco sale del componente.
   *
   * IMPORTANTE: cuando el usuario hace click en un ítem del dropdown, el blur dispara ANTES
   * que el click. Los ítems son <div> no focusables → relatedTarget=null → contains(null)=false
   * → isInsideComponent=false. Sin el flag _isMouseDownInPanel, el código allowFreeText
   * emitiría freeTextEntered("texto_escrito") y programaría applyValueToInput("texto_escrito")
   * a 200ms, sobreescribiendo el valor real seleccionado por onSelected.
   */
  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent) {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isInsideComponent = this.elementRef.nativeElement.contains(relatedTarget);

    // Si el blur fue causado por un click dentro del panel (ítem no focusable),
    // ignorar la lógica de "salida sin selección" — onSelected se encargará.
    if (this._isMouseDownInPanel) return;

    if (!isInsideComponent) {
      // Si allowFreeText y hay texto escrito sin haber seleccionado un item
      if (this.allowFreeText && this.currentQuery && this.currentQuery !== this.lastSelectedValue) {
        const freeText = this.currentQuery;
        this.lastSelectedValue = freeText;
        this.selectedValue = freeText;
        this.onChange(freeText);
        this.freeTextEntered.emit(freeText);
        // Guardar referencia para cancelarlo si onSelected llega a tiempo
        this._freeTextTimer = setTimeout(() => {
          this._freeTextTimer = null;
          this.applyValueToInput(freeText);
          this.cdr.detectChanges();
        }, 200);
      }

      setTimeout(() => {
        this.autoComplete?.close();
        this.cdr.detectChanges();
      }, 150);
    }
  }

  ngOnDestroy() {
    this._viewInitialized = false;
    if (this._freeTextTimer) { clearTimeout(this._freeTextTimer); }
    this.autoComplete?.close();
    this.internalData = [];
    this.backupData = [];
    this.currentQuery = '';
    this.lastSelectedValue = '';
  }

  /**
   * Detectar cambios en [data] desde el padre y forzar refresco del panel
   */
  ngOnChanges(changes: any) {
    if (changes['data'] && changes['data'].currentValue) {
      this.internalData = (changes['data'].currentValue as any[]).map(item => {
        if (typeof item !== 'object' || !item) return item;
        const cleaned: any = { ...item };
        Object.keys(cleaned).forEach(key => {
          if (typeof cleaned[key] === 'string') {
            cleaned[key] = cleaned[key].replace(/<[^>]*>/g, '');
          }
        });
        return cleaned;
      });
      this.backupData = [...this.internalData]; // guardar copia para restaurar tras limpiar
      this.cdr.detectChanges();
      // Abrir solo si hay texto activo en el input
      if (this.internalData.length > 0 && this.autoComplete && this.currentQuery.length > 0) {
        this.resetAndOpenPanel();
      }
    }
  }

  /**
   * Garantiza selectedIdx=-1 para que el ítem 0 siempre sea seleccionable.
   *
   * NO usamos close()+open() porque eso creaba una ventana ciega de ~50ms donde el panel
   * estaba cerrado. En la primera carga de datos ng-autocomplete ya abre el panel por su
   * cuenta (setInitialValue → select → isOpen=true). Si el usuario hacía click justo en ese
   * momento, nuestro close() lo impedía y el click caía fuera del dropdown mostrando el texto
   * escrito en lugar del ítem seleccionado. Solución: resetear selectedIdx directamente.
   */
  private resetAndOpenPanel() {
    setTimeout(() => {
      if (!this.autoComplete) return;
      (this.autoComplete as any)['selectedIdx'] = -1;
      const isOpen = (this.autoComplete as any)['isOpen'];
      if (!isOpen) {
        this.autoComplete.open();
        setTimeout(() => {
          if (this.autoComplete) {
            (this.autoComplete as any)['selectedIdx'] = -1;
          }
          this.cdr.detectChanges();
        }, 0);
      } else {
        this.cdr.detectChanges();
      }
    }, 0);
  }

  /** Focus: si hay datos previos reabre el panel directamente; si no hay datos pero hay query, relanza la búsqueda */
  onFocus() {
    // Si internalData fue limpiado (X) pero backupData tiene la última lista, restaurar
    if (this.internalData.length === 0 && this.backupData.length > 0) {
      this.internalData = [...this.backupData];
      this.cdr.detectChanges();
    }

    if (this.internalData.length > 0) {
      // Hay resultados — resetear índice y reabrir sin nueva petición
      this.resetAndOpenPanel();
      return;
    }

    // Sin datos ni backup: relanzar búsqueda si hay texto o valor previo
    const query = this.currentQuery || this.lastSelectedValue;
    if (query) {
      this.currentQuery = query;
      this.inputChanged.emit(query);
    }
  }

  /**
   * Función para renderizar el valor seleccionado en el input (texto plano)
   */
  selectedValueRender = (item: any): string => {
    if (!item) return '';
    if (typeof item === 'string') return item.replace(/<[^>]*>/g, '');
    return item[this.searchKeyword]
      ? String(item[this.searchKeyword]).replace(/<[^>]*>/g, '')
      : '';
  };

  /**
   * Filtro personalizado para el autocomplete.
   * Busca en TODOS los campos string del item para que funcione aunque el backend
   * haya filtrado por un campo distinto al searchKeyword (ej: RUC, código, etc.).
   */
  customFilter = (items: any[], query: string): any[] => {
    if (!query) return items;
    const lowerQuery = query.toLowerCase();
    return items.filter(item =>
      Object.values(item).some(
        val => typeof val === 'string' && val.toLowerCase().includes(lowerQuery)
      )
    );
  };

  /**
   * Cuando se selecciona un ítem del autocomplete.
   *
   * DOBLE DISPARO: ng-autocomplete.ngOnChanges ejecuta setInitialValue(initialValue) en CADA
   * cambio de @Input. Al asignar selectedValue aquí, Angular detecta que [initialValue] cambió
   * → ngOnChanges → setInitialValue(string) → select(string) → selected.emit(string) → segunda
   * llamada a onSelected. Guard 1 (string) y Guard 2 (_isSelecting) lo previenen.
   *
   * FREETEXT OVERRIDE: cuando allowFreeText=true, onFocusOut puede haber programado un
   * applyValueToInput con el texto escrito a los 200ms (porque blur dispara antes que click).
   * Cancelamos ese timer aquí para que no sobreescriba el valor real seleccionado.
   */
  onSelected(event: any) {
    // Guard 1: ng-autocomplete emite segunda vez con el string de visualización
    if (!event || typeof event === 'string') {
      return;
    }
    // Guard 2: re-entrada por setInitialValue → select → selected.emit
    if (this._isSelecting) {
      return;
    }
    this._isSelecting = true;

    // Cancelar el applyValueToInput("texto_escrito") de onFocusOut si aún no disparó
    if (this._freeTextTimer) {
      clearTimeout(this._freeTextTimer);
      this._freeTextTimer = null;
    }

    console.log('Item seleccionado:', event);

    if (event[this.searchKeyword]) {
      const plainText = String(event[this.searchKeyword]).replace(/<[^>]*>/g, '');
      this.selectedValue = plainText;
      this.currentQuery = '';
      this.lastSelectedValue = plainText;

      this.selected.emit(event);
      this.onChange(event);
      this.onTouched();

      setTimeout(() => {
        this.autoComplete?.close();
        this.selectedValue = plainText;
        this._isSelecting = false;
      }, 50);
    } else {
      this._isSelecting = false;
    }
  }

  /**
   * Cuando cambia el texto del input
   */
  onInputChanged(searchText: string) {
    if (this.uppercase && searchText) {
      searchText = searchText.toUpperCase();
    }
    this.currentQuery = searchText || '';
    this.selectedValue = searchText;
    this.onChange(searchText);

    // Si no hay texto o no alcanza el mínimo → NO emitir al padre (no llama al backend)
    if (!searchText || searchText.length < this.minQueryLength) {
      this.internalData = [];
      return;
    }

    this.inputChanged.emit(searchText);
  }

  /**
   * Cuando se limpia el input con la X
   * NO emite inputChanged (evita petición al backend con vacío)
   * Restaura el backupData para que al hacer focus vuelva a mostrar la última lista
   */
  onInputCleared() {
    this.selectedValue = '';
    this.currentQuery = '';
    this.lastSelectedValue = '';
    // Restaurar lista auxiliar para que focus pueda reabrirla sin nueva petición
    this.internalData = this.backupData.length > 0 ? [...this.backupData] : [];
    // Solo notificar al padre que se limpió (sin texto vacío que dispare backend)
    this.inputCleared.emit();
    this.onChange(null);
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    let plainText = '';
    if (value && typeof value === 'object' && value[this.searchKeyword]) {
      plainText = String(value[this.searchKeyword]).replace(/<[^>]*>/g, '');
    } else if (typeof value === 'string' && value) {
      plainText = value.replace(/<[^>]*>/g, '');
    }

    this.selectedValue = plainText;
    this.lastSelectedValue = plainText;

    if (this.autoComplete) {
      this.applyValueToInput(plainText);
    } else {
      // ViewChild aún no disponible, guardar para aplicar en ngAfterViewInit
      this.pendingValue = plainText;
    }
  }

  /** Fuerza el texto en el input nativo del ng-autocomplete */
  private applyValueToInput(text: string): void {
    setTimeout(() => {
      if (!this.autoComplete) return;
      // Acceder al input nativo interno de ng-autocomplete
      const inputEl: HTMLInputElement | null =
        (this.autoComplete as any)['searchInput']?.nativeElement ??
        (this.autoComplete as any)['inputEl']?.nativeElement ??
        this.elementRef.nativeElement.querySelector('input');
      if (inputEl) {
        inputEl.value = text;
      }
      this.selectedValue = text;
      this.cdr.detectChanges();
    }, 0);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
